// src/components/CompanyMaster.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import "./CompanyMaster.css";

// 勤務曜日ラベル（月=0〜日=6）と既定（月〜金）
const WEEKDAY_LABELS = ["月", "火", "水", "木", "金", "土", "日"];
const DEFAULT_WORKDAYS = [0, 1, 2, 3, 4];

const CompanyMaster = () => {
  const navigate = useNavigate();

  const [company, setCompany] = useState({
    name: "",
    address: "", 
    phone: "", 
    description: "",
    logo: "",
    fax: "",
    homepage: "",
    origin:"",
    privacy_mark:"",
    privacy_mark_image: "",
    custom_fields: [],
    use_employee_schedule: false,
    default_shift: null,
    auto_fill_fixed_shift: false,
    working_weekdays: [0, 1, 2, 3, 4],
    round_punch_to_15_minutes: false,
    overtime_requires_supervisor_approval: false,
    clock_in_early_minutes: 60,
    clock_out_grace_minutes: 30,
    punch_start_date: "",
    special_leave_list: [],
    paid_leave_lead_days: 14,
  });
  const [settings, setSettings] = useState({
    show_company_name: true,
    show_company_tel: true,
    show_company_logo: true,
    show_company_description: true,
  });
  const [shiftPresets, setShiftPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [cRes, pRes, sRes] = await Promise.all([
          api.get("/api/company-info/"),
          api.get("/api/shifts/presets/").catch(() => ({ data: [] })),
          api.get("/api/employee-card/display-setting/").catch(() => ({ data: {} })),
        ]);

        if (cRes?.data) {
          const data = { ...cRes.data };
          // 特別休暇リスト未設定なら初期項目（結婚・出産）を表示
          if (!Array.isArray(data.special_leave_list) || data.special_leave_list.length === 0) {
            data.special_leave_list = [
              { label: "結婚" },
              { label: "出産" },
            ];
          }
          if (!data.custom_fields || !Array.isArray(data.custom_fields)){
            data.custom_fields = [];
          }
          setCompany(prev => ({ ...prev, ...data }));
        }
        if (pRes?.data) setShiftPresets(Array.isArray(pRes.data) ? pRes.data : []);
        if (sRes?.data) setSettings(prev => ({ ...prev, ...sRes.data }));
      } catch (e) {
        console.error(e);
        setMsg("設定の読み込みでエラーが発生しました。");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;
    const key = id.replace("ecs-company-", "").replace("-", "_");
    setCompany(prev => ({ ...prev, [key]: value }));
  };
  // Privacy Markの画像
  const handlePrivacyImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCompany(prev => ({ ...prev, privacy_mark_image: event.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  //会社追加分
  const addCustomField = () => {
    setCompany(prev => ({
      ...prev,
      custom_fields: [...(prev.custom_fields || []), { label: "", value: "", image: null }]
    }));
  };

  const handleCustomFieldChange = (index, key, val) => {
    setCompany(prev => {
      const list = [...prev.custom_fields];
      list[index] = { ...list[index], [key]: val };
      return { ...prev, custom_fields: list };
    });
  };

  const handleCustomImageChange = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        handleCustomFieldChange(index, "image", event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // 追加分の削除
  const removeCustomField = (index) => {
    setCompany(prev => ({
      ...prev,
      custom_fields: prev.custom_fields.filter((_, i) => i !== index)
    }));
  };

  const toggle = (key) => setSettings(prev => ({ ...prev, [key]: !prev[key] }));

  const saveSettings = async () => {
    setSaving(true); setMsg("");
    try {
      const res = await api.put("/api/employee-card/display-setting/update/", settings);
      if (res?.data) setSettings(prev => ({ ...prev, ...res.data }));
      setMsg("表示設定を保存しました。");
    } catch (e) {
      console.error(e);
      setMsg("表示設定の保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  const formatSaveError = (e, label) => {
    const detail = e.response?.data;
    const hint =
      detail && typeof detail === "object"
        ? Object.entries(detail)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
            .join(" / ")
        : "";
    return hint ? `${label}に失敗しました。（${hint}）` : `${label}に失敗しました。`;
  };

  /** 指定フィールドだけ PUT（partial）。他の会社設定はサーバ上の値をそのまま維持 */
  const patchCompanyFields = async (fields, successMsg, errorLabel) => {
    setSaving(true);
    setMsg("");
    try {
      const res = await api.put("/api/company-info/update/", fields);
      if (res?.data) setCompany((prev) => ({ ...prev, ...res.data }));
      setMsg(successMsg);
    } catch (e) {
      console.error(e);
      setMsg(formatSaveError(e, errorLabel));
    } finally {
      setSaving(false);
    }
  };
  {/*}
  const saveCompany = async () => {
    setSaving(true);
    setMsg("");
    try {
      const { logo, ...payload } = company;
      let res;
      if (logo && String(logo).startsWith("data:")) {
        const form = new FormData();
        Object.entries(payload).forEach(([key, val]) => {
          if (val === undefined || val === null) return;
          if (typeof val === "boolean") form.append(key, val ? "true" : "false");
          else form.append(key, val);
        });
        const blob = await (await fetch(logo)).blob();
        form.append("logo", blob, "logo.png");
        res = await api.put("/api/company-info/update/", form);
      } else {
        res = await api.put("/api/company-info/update/", payload);
      }
      if (res?.data) setCompany((prev) => ({ ...prev, ...res.data }));
      setMsg("会社情報を保存しました。");
    } catch (e) {
      console.error(e);
      setMsg(formatSaveError(e, "会社情報の保存"));
    } finally {
      setSaving(false);
    }
  };
  */}
  const saveCompany = async () => {
    setSaving(true); setMsg("");
    try {
      const { logo, privacy_mark_image, custom_fields, ...payload } = company;
      const form = new FormData();

      // 
      Object.entries(payload).forEach(([key, val]) => {
        if (val === undefined || val === null) return;
        if (key === "custom_fields" || key === "special_leave_list" || key === "working_weekdays") {
          form.append(key, JSON.stringify(val));
        } else if (typeof val === "boolean") {
          form.append(key, val ? "true" : "false");
        } else {
          form.append(key, val);
        }
      });

      //会社情報追加
      const cleanedCustomFields = [];
      for (let i = 0; i < custom_fields.length; i++) {
        const field = custom_fields[i];
        const fieldItem = { label: field.label, value: field.value, image: null };

        //新しい画像をアップロード
        if (field.image && String(field.image).startsWith("data:")) {
          const blob = await (await fetch(field.image)).blob();
         
          form.append(`custom_image_${i}`, blob, `custom_img_${i}.png`);
          fieldItem.image = `__PENDING_FILE_INDEX_${i}__`;
        } else {
          //存在している画像
          fieldItem.image = field.image;
        }
        cleanedCustomFields.push(fieldItem);
      }
      form.append("custom_fields", JSON.stringify(cleanedCustomFields));

      // ロゴ画像を修理
      if (logo && String(logo).startsWith("data:")) {
        const blob = await (await fetch(logo)).blob();
        form.append("logo", blob, "logo.png");
      }
      // プライバシーマーク画像を修理
      if (privacy_mark_image && String(privacy_mark_image).startsWith("data:")) {
        const pMarkBlob = await (await fetch(privacy_mark_image)).blob();
        form.append("privacy_mark_image", pMarkBlob, "privacy_mark.png");
      }

      const res = await api.put("/api/company-info/update/", form, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (res?.data) {
        const data = { ...res.data };
        if (!data.custom_fields || !Array.isArray(data.custom_fields)) {
          data.custom_fields = [];
        }
        setCompany(prev => ({ ...prev, ...data }));
      }
      setMsg("会社情報を保存しました。");
    } catch (e) {
      console.error(e);
      setMsg(formatSaveError(e, "会社情報の保存"));
    } finally {
      setSaving(false);
    }
  };

  const savePunchSchedule = () => {
    let early = Number(company.clock_in_early_minutes);
    if (!Number.isFinite(early) || early < 0) early = 60;
    if (early > 240) early = 240; // 上限4時間
    let grace = Number(company.clock_out_grace_minutes);
    if (!Number.isFinite(grace) || grace < 0) grace = 30;
    if (grace > 240) grace = 240; // 上限4時間
    return patchCompanyFields(
      {
        use_employee_schedule: !!company.use_employee_schedule,
        default_shift: company.default_shift ?? null,
        auto_fill_fixed_shift: !!company.auto_fill_fixed_shift,
        working_weekdays: (company.working_weekdays && company.working_weekdays.length)
          ? company.working_weekdays
          : DEFAULT_WORKDAYS,
        round_punch_to_15_minutes: !!company.round_punch_to_15_minutes,
        clock_in_early_minutes: early,
        clock_out_grace_minutes: grace,
        punch_start_date: company.punch_start_date || "",
      },
      "打刻・スケジュール設定を保存しました。",
      "打刻・スケジュール設定の保存"
    );
  };

  const saveOvertimeWorkflow = () =>
    patchCompanyFields(
      {
        overtime_requires_supervisor_approval: !!company.overtime_requires_supervisor_approval,
      },
      "残業ワークフローを保存しました。",
      "残業ワークフローの保存"
    );

  const savePaidLeaveWorkflow = () => {
    // 空欄は Number("") が 0 になり、意図せず「制限なし」になってしまう。
    // 0 は「制限なし」という意味のある値なので、空欄とは区別して既定の14日に戻す。
    const raw = String(company.paid_leave_lead_days ?? "").trim();
    let lead = raw === "" ? 14 : Number(raw);
    if (!Number.isFinite(lead) || lead < 0) lead = 14;
    lead = Math.floor(lead);
    if (lead > 90) lead = 90;   // 上限3ヶ月
    return patchCompanyFields(
      { paid_leave_lead_days: lead },
      "有給ワークフローを保存しました。",
      "有給ワークフローの保存"
    );
  };

  // ===== 特別休暇リスト =====
  const updateSpecialLeaveItem = (idx, patch) =>
    setCompany((prev) => {
      const list = Array.isArray(prev.special_leave_list) ? [...prev.special_leave_list] : [];
      list[idx] = { ...list[idx], ...patch };
      return { ...prev, special_leave_list: list };
    });

  const addSpecialLeaveItem = () =>
    setCompany((prev) => ({
      ...prev,
      special_leave_list: [
        ...(Array.isArray(prev.special_leave_list) ? prev.special_leave_list : []),
        { label: "", content: "" },
      ],
    }));

  const removeSpecialLeaveItem = (idx) =>
    setCompany((prev) => ({
      ...prev,
      special_leave_list: (prev.special_leave_list || []).filter((_, i) => i !== idx),
    }));

  const saveSpecialLeaveList = () =>
    patchCompanyFields(
      {
        special_leave_list: (company.special_leave_list || [])
          .map((it) => ({ label: (it.label || "").trim(), content: (it.content || "").trim() }))
          .filter((it) => it.label !== ""),
      },
      "特別休暇リストを保存しました。",
      "特別休暇リストの保存"
    );

  // CSV取込: 1列目=ラベル, 2列目=規程の条文（ホバー表示する内容）
  const importSpecialLeaveCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // 同じファイルを連続選択できるようリセット
    if (!file) return;
    setSaving(true); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/api/company-info/special-leave/import-csv/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const list = res?.data?.special_leave_list;
      if (Array.isArray(list)) {
        setCompany((prev) => ({ ...prev, special_leave_list: list }));
      }
      const errs = res?.data?.errors || [];
      setMsg(
        `CSVから ${res?.data?.imported ?? list?.length ?? 0} 件を取り込みました。` +
        (errs.length ? `（${errs.length} 件の警告: ${errs.join(" / ")}）` : "")
      );
    } catch (err) {
      console.error(err);
      setMsg(err.response?.data?.detail || "CSVの取り込みに失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  // ShiftPreset の start_time / end_time は null 許可。表示時は "--:--" で安全表示する
  const fmtHm = (v) => (typeof v === "string" && v.length >= 5 ? v.substring(0, 5) : "--:--");

  if (loading) return <div className="ecs-loading">読み込み中…</div>;

  return (
    <div className="ecs-page">
      <header className="ecs-header">
        <div className="ecs-title-block">
          <h1 className="ecs-title">社員証・会社設定（管理）</h1>
          <p className="ecs-subtitle">
            社員証に表示する項目と、会社名・住所・電話番号などの登録を行います。表示のON/OFFは左欄、内容の編集は右欄から保存してください。
          </p>
        </div>
        {/*}
        <button type="button" className="ecs-btn-back" onClick={() => navigate("/admin/employee-card-setting")}>
          戻る
        </button>
        */}
        <button className="btn-back" onClick={() => navigate(-1)}>
         戻る
      </button>
      </header>

      <div className="ecs-grid">

        <section className="ecs-panel" aria-labelledby="ecs-panel-company">
          {/*}
          <h2 id="ecs-panel-company" className="ecs-panel-title">
            会社ブロック（表示と登録内容）
          </h2>
          <div className="ecs-toggle-list">
            {Object.entries({
              show_company_name: "社名",
              show_company_tel: "会社電話番号",
              show_company_logo: "ロゴ",
              show_company_description: "経営理念",
            }).map(([k, label]) => (
              <label key={k}>
                <input type="checkbox" checked={!!settings[k]} onChange={() => toggle(k)} />
                <span>{label}</span>
              </label>
            ))}
          </div>
          */}
          <h3 className="ecs-section-label">会社情報の登録</h3>
          <div className="ecs-form-row">
            <label htmlFor="ecs-company-logo">ロゴ</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
              <input
                id="ecs-company-logo"
                className="ecs-input"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      setCompany({ ...company, logo: e.target.result });
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
              {company.logo ? (
                <div className="ecs-logo-preview" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <img
                    src={company.logo}
                    alt="会社ロゴ"
                    style={{ maxWidth: 160, maxHeight: 80, objectFit: "contain", border: "1px solid #e2e8f0", borderRadius: 4, padding: 4, background: "#fff" }}
                  />
                  <span style={{ fontSize: "0.8rem", color: "#16a34a" }}>
                    {String(company.logo).startsWith("data:") ? "選択済み（保存で反映）" : "登録済み"}
                  </span>
                </div>
              ) : (
                <span style={{ fontSize: "0.8rem", color: "#94a3b8" }}>ロゴ未登録</span>
              )}
            </div>
          </div>
          <div className="ecs-form-row">
            <label htmlFor="ecs-company-name">社名</label>
            <input
              id="ecs-company-name"
              className={`ecs-input ${(!company.name || String(company.name).trim() === "") ? "ecs-input-blank" : ""}`}
              value={company.name ?? ""}
              onChange={(e) => setCompany({ ...company, name: e.target.value })}
            />
          </div>
          <div className="ecs-form-row">
            <label htmlFor="ecs-company-address">住所</label>
            <input
              id="ecs-company-address"
              className={`ecs-input ${(!company.address || String(company.address).trim() === "") ? "ecs-input-blank" : ""}`}
              value={company.address ?? ""}
              onChange={(e) => setCompany({ ...company, address: e.target.value })}
            />
          </div>
          <div className="ecs-form-row">
            <label htmlFor="ecs-company-phone">電話番号</label>
            <input
              id="ecs-company-phone"
              className={`ecs-input ${(!company.phone || String(company.phone).trim() === "") ? "ecs-input-blank" : ""}`}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="例: 03-1234-5678"
              value={company.phone ?? ""}
              onChange={(e) => setCompany({ ...company, phone: e.target.value })}
            />
          </div>
          <div className="ecs-form-row">
            <label htmlFor="ecs-company-fax">FAX</label>
            <input
              id="ecs-company-fax"
              className={`ecs-input ${(!company.fax || String(company.fax).trim() === "") ? "ecs-input-blank" : ""}`}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="例: 03-1234-5678"
              value={company.fax ?? ""}
              onChange={(e) => setCompany({ ...company, fax: e.target.value })}
            />
          </div>
          <div className="ecs-form-row">
            <label htmlFor="ecs-company-home">ホームページ</label>
            <input
              id="ecs-company-home"
              className={`ecs-input ${(!company.homepage || String(company.homepage).trim() === "") ? "ecs-input-blank" : ""}`}
              type="url"
              placeholder="例: https://www.example.com"
              value={company.homepage ?? ""}
              onChange={(e) => setCompany({ ...company, homepage: e.target.value })}
            />
          </div>
          <div className="ecs-form-row">
            <label htmlFor="ecs-company-desc">経営理念</label>
            <textarea
              id="ecs-company-desc"
              className={`ecs-textarea ${(!company.description || String(company.description).trim() === "") ? "ecs-input-blank" : ""}`}
              value={company.description ?? ""}
              onChange={(e) => setCompany({ ...company, description: e.target.value })}
            />
          </div> 
          <div className="ecs-form-row">
            <label htmlFor="ecs-company-origin">社名の由来</label>
            <textarea
              id="ecs-company-origin"
              className={`ecs-textarea ${(!company.origin || String(company.origin).trim() === "") ? "ecs-input-blank" : ""}`}
              value={company.origin ?? ""}
              onChange={(e) => setCompany({ ...company, origin: e.target.value })}
            />
          </div>
          {/*}
          <div className="ecs-form-row">
            <label htmlFor="ecs-company-privacy">プライバシーマークNo</label>
            <input
              id="ecs-company-privacy"
              className="ecs-input"
              value={company.privacy_mark ?? ""}
              onChange={(e) => setCompany({ ...company, privacy_mark: e.target.value })}
            />
          </div>
          */}
          <div className="ecs-pmark-container">
            <div className="ecs-pmark-input-side">
              <label htmlFor="ecs-company-privacy">プライバシーマークNo</label>
              <input
                id="ecs-company-privacy"
                className={company.privacy_mark ? "ecs-input" : "ecs-input ecs-input-blank"}
                placeholder="例: 第12345678号"
                value={company.privacy_mark ?? ""}
                //onChange={handleChange}
                onChange={(e) => setCompany({ ...company, privacy_mark: e.target.value })}
              />
            </div>
            <div className="ecs-pmark-image-side">
              <label>プライバシーマーク画像</label>
              <div className="ecs-pmark-uploader-box">
                <input
                  id="pmark-image-file"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handlePrivacyImageChange}
                />
                <label htmlFor="pmark-image-file" className="ecs-pmark-upload-trigger">
                  {company.privacy_mark_image ? "画像を変更" : "📄 選択"}
                </label>
                {company.privacy_mark_image && (
                  <div className="ecs-pmark-mini-preview">
                    <img src={company.privacy_mark_image} alt="P-Mark Preview" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 会社情報追加(CUSTOM FIELDS) */}
          <div className="ecs-custom-fields-wrapper">
            <div className="ecs-custom-fields-header">
              <h4 className="ecs-custom-title">➕ 自由追加情報 (会社独自項目)</h4>
              <button type="button" className="ecs-btn-add-custom" onClick={addCustomField}>
                ＋ 項目を追加
              </button>
            </div>
            
            {(!company.custom_fields || company.custom_fields.length === 0) ? (
              <p className="ecs-custom-empty-text">追加された項目はありません。上のボタンから自由に追加できます。</p>
            ) : (
              <div className="ecs-custom-fields-list">
                {company.custom_fields.map((item, index) => (
                  <div key={index} className="ecs-custom-fields-card">
                    <div className="ecs-custom-fields-inputs-row">
                      <input
                        type="text"
                        className="ecs-input ecs-custom-label-input"
                        placeholder="項目名 (例: 資本金)"
                        value={item.label || ""}
                        onChange={(e) => handleCustomFieldChange(index, "label", e.target.value)}
                      />
                      <input
                        type="text"
                        className={item.value ? "ecs-input ecs-custom-value-input" : "ecs-input ecs-custom-value-input ecs-input-blank"}
                        placeholder="内容を入力"
                        value={item.value || ""}
                        onChange={(e) => handleCustomFieldChange(index, "value", e.target.value)}
                      />
                      <button
                        type="button"
                        className="ecs-btn-delete-custom"
                        onClick={() => removeCustomField(index)}
                      >
                        削除
                      </button>
                    </div>

                    
                    <div className="ecs-custom-image-row">
                      <input
                        id={`custom-file-input-${index}`}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handleCustomImageChange(index, e)}
                      />
                      <label htmlFor={`custom-file-input-${index}`} className="ecs-custom-img-btn">
                        {item.image ? "🖼️ 添付画像を変更" : "📎 画像を添付"}
                      </label>
                      {item.image && (
                        <div className="ecs-custom-img-preview">
                          <img src={item.image} alt="Attachment Preview" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <p style={{ fontSize: "0.8rem", color: "#6b7280", margin: "8px 0 0" }}>
            社名・住所・電話・FAX・HP・経営理念・由来・プライバシーマーク・ロゴをまとめて保存します。
          </p>
          <div className="ecs-actions" style={{ flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            <button
              type="button"
              className="ecs-btn-primary"
              onClick={saveCompany}
              disabled={saving}
            >
              {saving ? "保存中…" : "会社情報保存"}
            </button>
          </div>

          {/*}
          <h3 className="ecs-section-label">スケジュール（打刻・勤怠）</h3>
          <div className="ecs-toggle-list">
            <label>
              <input
                type="checkbox"
                checked={!!company.use_employee_schedule}
                onChange={(e) => setCompany({ ...company, use_employee_schedule: e.target.checked })}
              />
              <span>
                社員がスケジュールを登録する（ON: 月間シフトを各自登録。OFF: 会社の固定シフト1種のみ）
              </span>
            </label>
          </div>
          {!company.use_employee_schedule && (
            <div className="ecs-form-row">
              <label htmlFor="ecs-default-shift">固定シフト（OFF時に全員に適用）</label>
              <select
                id="ecs-default-shift"
                className="ecs-select"
                value={company.default_shift != null ? String(company.default_shift) : ""}
                onChange={(e) =>
                  setCompany({ ...company, default_shift: e.target.value ? Number(e.target.value) : null })
                }
              >
                <option value="">— 未選択 —</option>
                {shiftPresets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code}: {p.name}({fmtHm(p.start_time)} ～ {fmtHm(p.end_time)})
                  </option>
                ))}
              </select>
              <button 
                type="button" 
                onClick={() => navigate("/shifts")} 
                style={{ whiteSpace: 'nowrap', padding: '5px 10px' }}
              >
                シフト追加
              </button>
            </div>
          )}

          <h3 className="ecs-section-label">打刻（勤怠）</h3>
          <div className="ecs-toggle-list">
            <label>
              <input
                type="checkbox"
                checked={!!company.round_punch_to_15_minutes}
                onChange={(e) => setCompany({ ...company, round_punch_to_15_minutes: e.target.checked })}
              />
              <span>出社・退社の記録時刻を15分単位に丸める（ローカル時刻で最寄りの15分へ四捨五入）</span>
            </label>
          </div>

          <div className="ecs-actions">
            <button type="button" className="ecs-btn-primary" onClick={saveSettings} disabled={saving}>
              {saving ? "保存中…" : "表示設定を保存"}
            </button>
            <button type="button" className="ecs-btn-secondary" onClick={saveCompany} disabled={saving}>
              {saving ? "保存中…" : "会社情報を保存"}
            </button>
          </div>
          */}
        </section>
         <section className="ecs-panel" aria-labelledby="ecs-panel-company">
          <h3 className="ecs-section-label">スケジュール（打刻・勤怠）</h3>
          <div className="ecs-toggle-list">
            <label>
              <input
                type="checkbox"
                checked={!!company.use_employee_schedule}
                onChange={(e) => setCompany({ ...company, use_employee_schedule: e.target.checked })}
              />
              <span>
                社員がスケジュールを登録する（ON: 月間シフトを各自登録。OFF: 会社の固定シフト1種のみ）
              </span>
            </label>
          </div>
          {!company.use_employee_schedule && (
            <div className="ecs-form-row">
              <label htmlFor="ecs-default-shift">固定シフト（OFF時に全員に適用）</label>
              <select
                id="ecs-default-shift"
                className="ecs-select"
                value={company.default_shift != null ? String(company.default_shift) : ""}
                onChange={(e) =>
                  setCompany({ ...company, default_shift: e.target.value ? Number(e.target.value) : null })
                }
              >
                <option value="">— 未選択 —</option>
                {shiftPresets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code}: {p.name}({fmtHm(p.start_time)} ～ {fmtHm(p.end_time)})
                  </option>
                ))}
              </select>
              <button 
                type="button" 
                onClick={() => navigate("/shifts")} 
                style={{ whiteSpace: 'nowrap', padding: '5px 10px' }}
              >
                シフト追加
              </button>
            </div>
          )}

          <h3 className="ecs-section-label">打刻（勤怠）</h3>
          <div className="ecs-toggle-list">
            <label>
              <input
                type="checkbox"
                checked={!!company.round_punch_to_15_minutes}
                onChange={(e) => setCompany({ ...company, round_punch_to_15_minutes: e.target.checked })}
              />
              <span>出社・退社の記録時刻を15分単位に丸める（ローカル時刻で最寄りの15分へ四捨五入）</span>
            </label>
          </div>
          {company.use_employee_schedule && (
            <div className="ecs-autofill">
              <label className="ecs-autofill-toggle">
                <input
                  type="checkbox"
                  checked={!!company.auto_fill_fixed_shift}
                  onChange={(e) => setCompany({ ...company, auto_fill_fixed_shift: e.target.checked })}
                />
                <span>
                  固定シフトを全社員に自動反映（勤務曜日に既定として表示・打刻可。休みにする日は各自「休日」シフトを割り当て）
                </span>
              </label>
              {company.auto_fill_fixed_shift && (
                <>
                  <div className="ecs-autofill-shift">
                    <label htmlFor="ecs-default-shift-auto" className="ecs-autofill-shift-label">固定シフト</label>
                    <select
                      id="ecs-default-shift-auto"
                      className="ecs-select"
                      value={company.default_shift != null ? String(company.default_shift) : ""}
                      onChange={(e) =>
                        setCompany({ ...company, default_shift: e.target.value ? Number(e.target.value) : null })
                      }
                    >
                      <option value="">— 未選択 —</option>
                      {shiftPresets.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.code}: {p.name}({fmtHm(p.start_time)} ～ {fmtHm(p.end_time)})
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="ecs-shift-add-btn"
                      onClick={() => navigate("/shifts")}
                    >
                      シフト追加
                    </button>
                  </div>
                  <div className="ecs-weekday-row">
                    <span className="ecs-weekday-title">勤務曜日：</span>
                    {WEEKDAY_LABELS.map((lbl, idx) => {
                      const cur = (company.working_weekdays && company.working_weekdays.length)
                        ? company.working_weekdays : DEFAULT_WORKDAYS;
                      return (
                        <label key={idx} className="ecs-weekday">
                          <input
                            type="checkbox"
                            checked={cur.includes(idx)}
                            onChange={(e) => {
                              const next = new Set(cur);
                              if (e.target.checked) next.add(idx); else next.delete(idx);
                              setCompany({ ...company, working_weekdays: [...next].sort((a, b) => a - b) });
                            }}
                          />
                          {lbl}
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="ecs-form-row" style={{ alignItems: "center", gap: 8 }}>
            <label htmlFor="ecs-clock-in-early">出勤打刻を許可する開始（始業の何分前から）</label>
            <input
              id="ecs-clock-in-early"
              type="number"
              min={0}
              max={240}
              step={15}
              className="ecs-select"
              style={{ width: 100 }}
              value={company.clock_in_early_minutes ?? 60}
              onChange={(e) => setCompany({ ...company, clock_in_early_minutes: e.target.value })}
            />
            <span style={{ color: "#6b7280" }}>分前（既定60分・上限240分）</span>
          </div>
          <p style={{ fontSize: "0.8rem", color: "#6b7280", margin: "4px 0 0" }}>
            出社ボタンがこの分数前から有効になります（例: 60＝1時間前、120＝2時間前）。労働時間はシフト基準で計算されるため、早く打刻しても早出が自動加算されることはありません。
          </p>

          <div className="ecs-form-row" style={{ alignItems: "center", gap: 8, marginTop: 12 }}>
            <label htmlFor="ecs-clock-out-grace">退勤打刻忘れとみなすまでの猶予（終業の何分後から）</label>
            <input
              id="ecs-clock-out-grace"
              type="number"
              min={0}
              max={240}
              step={15}
              className="ecs-select"
              style={{ width: 100 }}
              value={company.clock_out_grace_minutes ?? 30}
              onChange={(e) => setCompany({ ...company, clock_out_grace_minutes: e.target.value })}
            />
            <span style={{ color: "#6b7280" }}>分後（既定30分・上限240分）</span>
          </div>
          <p style={{ fontSize: "0.8rem", color: "#6b7280", margin: "4px 0 0" }}>
            退社予定時刻からこの分数を過ぎても退勤打刻が無い場合に、本人の社員証画面と上司の「部下の打刻忘れ」に表示します（例: 30＝30分後、15＝15分後）。残業申請を出している日は、その残業終了予定時刻を起点にします。
          </p>

          <div className="ecs-form-row" style={{ alignItems: "center", gap: 8, marginTop: 12 }}>
            <label htmlFor="ecs-punch-start-date">打刻運用開始日</label>
            <input
              id="ecs-punch-start-date"
              type="date"
              className="ecs-select"
              style={{ width: 170 }}
              value={company.punch_start_date ?? ""}
              onChange={(e) => setCompany({ ...company, punch_start_date: e.target.value })}
            />
          </div>
          <p style={{ fontSize: "0.8rem", color: "#6b7280", margin: "4px 0 0" }}>
            このシステムで打刻を開始した日。これより前の日は打刻が無くて当然なので、未打刻による出社ブロック・打刻忘れ検知・社員の自己修正の対象外になります（旧システムからの移行期間用）。空欄なら全期間が対象です。
          </p>

          {/* 打刻・スケジュールの保存ボタンは、必ずその設定群の直後に置く。
              下の「残業ワークフロー保存」は別項目しか送らないため、離して並べると
              15分丸めなどをチェックしてもそちらを押してしまい、保存されないまま
              「保存しました」と出てしまう。 */}
          <div className="ecs-actions" style={{ marginTop: 12 }}>
            <button type="button" className="ecs-btn-primary" onClick={savePunchSchedule} disabled={saving}>
              {saving ? "保存中…" : "打刻・スケジュール保存"}
            </button>
          </div>

          <h3 className="ecs-section-label">申請ワークフロー（残業）</h3>
          <div className="ecs-toggle-list">
            <label>
              <input
                type="checkbox"
                checked={!!company.overtime_requires_supervisor_approval}
                onChange={(e) =>
                  setCompany({ ...company, overtime_requires_supervisor_approval: e.target.checked })
                }
              />
              <span>
                残業時に上司の承認を得る
                （ON: 部下が事前申請 → 直属上司が承認/却下 → 申請者が本申請ボタンで確定。
                OFF: 上司には情報共有の通知のみ、承認/却下ボタンは表示せず管理者承認のみ）
              </span>
            </label>
          </div>
          <p style={{ fontSize: "0.8rem", color: "#6b7280", margin: "8px 0 0" }}>
            残業ワークフローのみ変更する場合は「残業ワークフローを保存」を押してください（社名・ロゴなどは変更されません）。
          </p>
          <div className="ecs-actions" style={{ flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            <button type="button" className="ecs-btn-primary" onClick={saveOvertimeWorkflow} disabled={saving}>
              {saving ? "保存中…" : "残業ワークフロー保存"}
            </button>
          </div>

          <h3 className="ecs-section-label">申請ワークフロー（有給）</h3>
          <div className="ecs-form-row" style={{ alignItems: "center", gap: 8 }}>
            <label htmlFor="ecs-paid-leave-lead">有給申請の締切（取得日の何日前まで）</label>
            <input
              id="ecs-paid-leave-lead"
              type="number"
              min={0}
              max={90}
              step={1}
              className="ecs-select"
              style={{ width: 100 }}
              value={company.paid_leave_lead_days ?? 14}
              onChange={(e) => setCompany({ ...company, paid_leave_lead_days: e.target.value })}
            />
            <span style={{ color: "#6b7280" }}>日前まで（0 = 制限なし・既定14日・上限90日）</span>
          </div>
          <p style={{ fontSize: "0.8rem", color: "#6b7280", margin: "4px 0 0" }}>
            社員の有給休暇申請フォームで選べる、最も早い取得日を決めます。
            14 なら「2週間前までに申請」、0 なら当日以降いつでも申請できます。
            理由が「病欠」の場合は、この設定に関わらず当日から申請できます。
          </p>
          {/* 入力欄と保存ボタンは必ず隣接させること。離すと別項目の保存ボタンを押してしまい、
              「保存しました」と出るのに保存されない事故が起きる。 */}
          <div className="ecs-actions" style={{ marginTop: 10 }}>
            <button type="button" className="ecs-btn-primary" onClick={savePaidLeaveWorkflow} disabled={saving}>
              {saving ? "保存中…" : "有給ワークフロー保存"}
            </button>
          </div>

          <h3 className="ecs-section-label">特別休暇リスト</h3>
          <p style={{ fontSize: "0.8rem", color: "#6b7280", margin: "0 0 8px" }}>
            社員の特別休暇申請画面で選べる項目です。「規程の条文」は、社員が理由を選んだときに下に表示されます。条文欄では Enter で改行でき、改行はそのまま社員側にも反映されます。CSVで一括取込もできます（1列目=名称、2列目=規程の条文）。
          </p>
          <div className="ecs-special-leave-list">
            {(company.special_leave_list || []).map((item, idx) => (
              <div
                key={idx}
                className="ecs-special-leave-row"
                style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6, width: "100%" }}
              >
                <input
                  type="text"
                  className="ecs-input"
                  style={{ flex: "0 0 140px", minWidth: 0, marginTop: 2 }}
                  placeholder="名称 例: 結婚"
                  value={item.label ?? ""}
                  onChange={(e) => updateSpecialLeaveItem(idx, { label: e.target.value })}
                />

                <textarea
                  className="ecs-input"
                  style={{ flex: "1 1 auto", minWidth: 0, minHeight: 60, resize: "vertical", lineHeight: 1.6 }}
                  rows={3}
                  placeholder={"規程の条文（改行可）例:\n本人が結婚するとき：…\n子が結婚するとき：…"}
                  value={item.content ?? ""}
                  onChange={(e) => updateSpecialLeaveItem(idx, { content: e.target.value })}
                />

                <button
                  type="button"
                  className="ecs-btn-secondary"
                  style={{ flexShrink: 0, whiteSpace: "nowrap", marginTop: 2, backgroundColor: "#fff0f0", color: "#e53e3e", borderColor: "#fed7d7" }}
                  onClick={() => removeSpecialLeaveItem(idx)}
                >
                  削除
                </button>
              </div>
            ))}
          </div>
          <div className="ecs-actions" style={{ flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            <button type="button" className="ecs-btn-secondary" onClick={addSpecialLeaveItem} disabled={saving}>
              ＋ 項目を追加
            </button>
            <label className="ecs-btn-secondary" style={{ cursor: "pointer", margin: 0 }}>
              CSV取込
              <input
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                disabled={saving}
                onChange={importSpecialLeaveCsv}
              />
            </label>
            <button type="button" className="ecs-btn-primary" onClick={saveSpecialLeaveList} disabled={saving}>
              {saving ? "保存中…" : "特別休暇リスト保存"}
            </button>
          </div>
         </section>
      </div>

      {msg ? <div className="ecs-msg" role="status">{msg}</div> : null}
    </div>
  );
};

export default CompanyMaster;
